(() => {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const initSlider = (root) => {
    // Use a runtime property instead of a data attribute. Shopify can replace
    // section markup after page load while preserving data attributes, which
    // would otherwise leave the new gallery without click handlers.
    if (root.alphaSliderReady === true) return;
    root.alphaSliderReady = true;

    const track = root.querySelector('[data-alpha-slider-track]');
    const viewport = root.querySelector('[data-alpha-slider-viewport]');
    const slides = [...root.querySelectorAll('[data-alpha-slide]')];
    const dots = [...root.querySelectorAll('[data-alpha-slider-dot]')];
    const counter = root.querySelector('[data-alpha-slider-counter]');
    const dialog = root.querySelector('[data-alpha-zoom-dialog]');
    const zoomImage = root.querySelector('[data-alpha-zoom-image]');
    const zoomScene = root.querySelector('[data-alpha-zoom-scene]');
    const zoomValue = root.querySelector('[data-alpha-zoom-value]');
    const zoomRange = root.querySelector('[data-alpha-zoom-range]');
    const zoomCanvas = root.querySelector('[data-alpha-zoom-canvas]');
    let index = 0;
    let pointerStart = null;
    let scale = 1;
    let panX = 0;
    let panY = 0;
    let zoomPointer = null;
    let zoomTarget = zoomImage;

    if (!track || !slides.length) return;

    const hydrateSlide = (slide) => {
      if (!slide) return;
      slide.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      });
      slide.querySelectorAll('img[data-src]').forEach((image) => {
        if (image.dataset.srcset) {
          image.srcset = image.dataset.srcset;
          image.removeAttribute('data-srcset');
        }
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      });
    };

    const updateZoom = () => {
      if (!zoomTarget) return;
      zoomTarget.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`;
      zoomTarget.classList.toggle('is-zoomed', scale > 1);
      if (zoomValue) zoomValue.textContent = `${Math.round(scale * 100)} %`;
      if (zoomRange) zoomRange.value = String(Math.round(scale * 100));
    };

    const resetZoom = (nextScale = 1) => {
      scale = nextScale;
      panX = 0;
      panY = 0;
      updateZoom();
    };

    const setZoomContent = () => {
      const slide = slides[index];
      const sceneContent = slide?.querySelector('[data-alpha-scene-content]');
      if (slide?.dataset.alphaScene === 'true' && sceneContent && zoomScene) {
        if (zoomImage) zoomImage.hidden = true;
        zoomScene.hidden = false;
        zoomScene.innerHTML = sceneContent.outerHTML;
        zoomTarget = zoomScene;
      } else {
        if (zoomScene) {
          zoomScene.hidden = true;
          zoomScene.innerHTML = '';
        }
        if (zoomImage) {
          zoomImage.hidden = false;
          zoomImage.src = slide?.dataset.alphaImageSrc || '';
        }
        zoomTarget = zoomImage;
      }
    };

    const update = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      hydrateSlide(slides[index]);
      track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
      slides.forEach((slide, slideIndex) => slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true'));
      dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false'));
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
      if (dialog?.open) {
        setZoomContent();
        resetZoom();
      }
    };

    const changeScale = (amount) => {
      scale = clamp(Number((scale + amount).toFixed(2)), 1, 3);
      if (scale === 1) {
        panX = 0;
        panY = 0;
      }
      updateZoom();
    };

    root.querySelector('[data-alpha-slider-prev]')?.addEventListener('click', () => update(index - 1));
    root.querySelector('[data-alpha-slider-next]')?.addEventListener('click', () => update(index + 1));
    dots.forEach((dot) => dot.addEventListener('click', () => update(Number(dot.dataset.alphaSliderDot))));

    viewport?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') update(index - 1);
      if (event.key === 'ArrowRight') update(index + 1);
    });

    viewport?.addEventListener('pointerdown', (event) => {
      pointerStart = event.clientX;
      viewport.setPointerCapture?.(event.pointerId);
    });

    viewport?.addEventListener('pointerup', (event) => {
      if (pointerStart === null) return;
      const distance = event.clientX - pointerStart;
      pointerStart = null;
      if (Math.abs(distance) < 45) return;
      update(index + (distance < 0 ? 1 : -1));
    });

    root.querySelector('[data-alpha-zoom-open]')?.addEventListener('click', () => {
      if (!dialog || (!zoomImage && !zoomScene)) return;
      setZoomContent();
      resetZoom();
      dialog.showModal();
    });

    root.querySelector('[data-alpha-zoom-close]')?.addEventListener('click', () => dialog?.close());
    root.querySelector('[data-alpha-zoom-in]')?.addEventListener('click', () => changeScale(.25));
    root.querySelector('[data-alpha-zoom-out]')?.addEventListener('click', () => changeScale(-.25));
    root.querySelector('[data-alpha-zoom-reset]')?.addEventListener('click', () => resetZoom());
    root.querySelector('[data-alpha-zoom-prev]')?.addEventListener('click', () => update(index - 1));
    root.querySelector('[data-alpha-zoom-next]')?.addEventListener('click', () => update(index + 1));
    zoomRange?.addEventListener('input', () => resetZoom(Number(zoomRange.value) / 100));

    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    zoomCanvas?.addEventListener('wheel', (event) => {
      event.preventDefault();
      changeScale(event.deltaY < 0 ? .25 : -.25);
    }, { passive: false });

    zoomCanvas?.addEventListener('dblclick', () => resetZoom(scale > 1 ? 1 : 2));

    zoomCanvas?.addEventListener('pointerdown', (event) => {
      if (scale <= 1) return;
      zoomPointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      zoomCanvas.setPointerCapture?.(event.pointerId);
      zoomCanvas.classList.add('is-panning');
    });

    zoomCanvas?.addEventListener('pointermove', (event) => {
      if (!zoomPointer || zoomPointer.id !== event.pointerId) return;
      panX += event.clientX - zoomPointer.x;
      panY += event.clientY - zoomPointer.y;
      zoomPointer.x = event.clientX;
      zoomPointer.y = event.clientY;
      updateZoom();
    });

    const stopPanning = () => {
      zoomPointer = null;
      zoomCanvas?.classList.remove('is-panning');
    };
    zoomCanvas?.addEventListener('pointerup', stopPanning);
    zoomCanvas?.addEventListener('pointercancel', stopPanning);

    dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') update(index - 1);
      if (event.key === 'ArrowRight') update(index + 1);
      if (event.key === '0') resetZoom();
    });

    update(0);
  };

  const init = () => document.querySelectorAll('[data-alpha-product-slider]').forEach(initSlider);
  document.addEventListener('DOMContentLoaded', init, { once: true });
  document.addEventListener('shopify:section:load', init);
  if (document.readyState !== 'loading') init();

  // Theme sections and localization components may be replaced after the
  // initial DOMContentLoaded event. Reconnect the gallery to any fresh markup.
  new MutationObserver(init).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
